/*
 that cannot be used from node. Put all shared helper functions into shared-utils.!
*/

import { afterNextRender, ChangeDetectorRef, DestroyRef, Injector } from '@angular/core';

/** Custom wrapper to use `requestAnimationFrame()` with `await`.
 * Returns the current time in ms, relative to "time origin" of the document. */
export async function animationFrame(): Promise<number /* DOMHighResTimeStamp */> {
  return new Promise<number>(resolve => requestAnimationFrame(time_ms => resolve(time_ms)));
}

/** How long any capped frame wait is willing to wait before giving up on the frame.
 *
 * A browser stops servicing `requestAnimationFrame` while it is not drawing — a
 * backgrounded WebView, a hidden tab. A plain frame wait taken at the wrong moment
 * therefore never resolves, and whatever the caller was going to undo afterwards is
 * never undone. Every caller here is undoing something visible, so being stuck is a
 * user-visible bug; waiting a little too long is not.
 *
 * The trade-off in the value: if this fires before a frame really was drawn (a main
 * thread stall longer than this), the caller's work silently degrades to a no-op for
 * that one attempt. So it sits well above any ordinary stall, and its only job is to
 * stop a caller being stranded. */
export const FRAME_WAIT_FALLBACK_MS = 500;

/** Wait for the next drawn frame, or for `FRAME_WAIT_FALLBACK_MS`, whichever comes first.
 *
 * Use this instead of a bare `animationFrame()` whenever the code after the wait MUST
 * run — see the constant above for why a bare wait can hang forever.
 *
 * Plain `setTimeout` rather than a destroy-guarded one on purpose: the timer is cleared
 * immediately after the race, and firing late could only resolve a promise nobody is
 * waiting on any more. */
export async function waitForDrawnFrameOrTimeoutAsync(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    animationFrame(),
    new Promise<void>(resolve => { timer = setTimeout(resolve, FRAME_WAIT_FALLBACK_MS); }),
  ]);
  clearTimeout(timer);
}

/** The two things a render wait has to watch, expressed without Angular so the rule
 * below can be unit tested. `waitForAngularChangeDetection` supplies the real ones.
 *
 * Both `onNextRender` and `onDestroy` return a function that undoes the registration,
 * so whichever one loses the race can be taken back off. */
export interface RenderOrDestroyHooks {
  /** the component has already gone, so there will never be a next render for it */
  alreadyDestroyed: boolean;
  /** run this after the component's next render; returns its undo function */
  onNextRender(run: () => void): () => void;
  /** run this when the component is destroyed; returns its undo function */
  onDestroy(run: () => void): () => void;
}

/** Wait for the next render, or for the component to be destroyed, whichever happens
 * first — and never for both, and never for neither.
 *
 * The destroyed half is the whole point, and it is not a nicety: without it this wait
 * never finishes at all when the component goes away, rather than merely finishing late.
 * Why Angular behaves that way, and what it cost (df-3f0eaff27ac6), is in
 * `design-docs/typescript-patterns.md` under "waitForAngularUpdate — ALWAYS use safe
 * version in components".
 *
 * Split out from `waitForAngularChangeDetection` below so it can be driven from a plain
 * node test — the same reason `providers/page-lifecycle-guard.ts` is a separate module. */
export function waitForRenderOrDestroyAsync(hooks: RenderOrDestroyHooks): Promise<void> {
  // registering an after-render callback on an already-destroyed component THROWS in
  // Angular, so this is not just a shortcut — asking at all is the error
  if (hooks.alreadyDestroyed) {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    let undoRenderWatch: (() => void) | undefined;
    let undoDestroyWatch: (() => void) | undefined;
    let settled = false;
    const settle = () => {
      // both hooks can fire in the same turn, and resolving twice would be a silent
      // no-op that hides a double registration, so say once and mean it
      if (settled) return;
      settled = true;
      // take BOTH registrations back off, so nothing is left attached to a component
      // that has already answered. Both rather than just the loser, because this does
      // not need to know which one won and undoing the winner costs nothing: Angular
      // has already dropped the fired render hook, and it explicitly allows a destroy
      // hook to unregister itself while the destroy hooks are running
      undoRenderWatch?.();
      undoDestroyWatch?.();
      resolve();
    };
    undoRenderWatch = hooks.onNextRender(settle);
    undoDestroyWatch = hooks.onDestroy(settle);
  });
}

/** Wait for angular change detection to complete, so the values are applied to the DOM.
 * Note: this does NOT mean that the DOM has completed the layout yet!
 * If you need to read styles, height, etc - you should waitForAngularUpdate() below!
 *
 * Returns as soon as the component is destroyed, rather than waiting for a render that
 * is never going to come — see `waitForRenderOrDestroyAsync` above for why that matters.
 * So the code after this wait CAN run on a destroyed component, where it used to hang:
 * check `viewDestroyed` / `destroyGuard.isDestroyed` after the wait if what follows must
 * not run then.
 * @param injector Injector to provide injection context for afterNextRender */
export async function waitForAngularChangeDetection(injector: Injector): Promise<void> {
  const destroyRef = injector.get(DestroyRef);
  return waitForRenderOrDestroyAsync({
    alreadyDestroyed: destroyRef.destroyed,
    onNextRender: run => {
      const renderHook = afterNextRender({ read: run }, { injector });
      return () => renderHook.destroy();
    },
    onDestroy: run => destroyRef.onDestroy(run),
  });
}

/** Helper to wait for Angular to fully update the DOM.
 *
 * Use this when you need to ensure DOM changes are fully applied, and that you can read
 * properties of elements, like height etc, before continuing.
 *
 * This cannot outlive the component: it returns as soon as the component is destroyed,
 * and its frame wait is capped. Read the note on `waitForAngularChangeDetection` above
 * before relying on what comes after the wait.
 * @param injector Injector that provides both ChangeDetectorRef and injection context */
export async function waitForAngularUpdate(injector: Injector): Promise<void> {
  // Get ChangeDetectorRef from the injector
  const cdr = injector.get(ChangeDetectorRef);
  // initiate change detection
  cdr.markForCheck();
  // wait for Angular to process the change detection cycle, copying values to the DOM
  await waitForAngularChangeDetection(injector);
  // wait for the browser to process the changes, do layout, etc. — capped, because a
  // browser that is not drawing never hands out a frame, and an uncapped wait here would
  // put back the exact hang the destroyed half above was added to remove.
  //
  // The cap is not free, and the cost lands on live components too: if the main thread
  // stalls for longer than FRAME_WAIT_FALLBACK_MS, the timer can win the race even though
  // a frame was going to be drawn, and a caller that reads layout after this wait
  // (offsetHeight, scroll position, element geometry) then reads it a frame early. That is
  // the trade the constant's own comment describes, taken deliberately: a stale reading on
  // a stalled main thread is recoverable, and a wait that never returns is not.
  await waitForDrawnFrameOrTimeoutAsync();
}

export async function waitHtmlEvent(element: HTMLElement, eventName: string): Promise<Event> {
  return new Promise<Event>(resolve => {
    let listener = (e: Event) => {
      element.removeEventListener(eventName, listener);
      resolve(e);
    };
    element.addEventListener(eventName, listener);
  });
}

export function isInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect();

  let isInView = (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.top + el.clientHeight * 0.5 <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left + el.clientWidth * 0.5 <= (window.innerWidth || document.documentElement.clientWidth)
  );

  // returns true or false based on whether or not the element is in viewport
  return isInView;
}

export function findParentScrollElement(element: HTMLElement): HTMLElement {
  if (!element) {
    return null;
  }
  if (element.classList.contains('scroll-content')) {
    return element;
  }
  return findParentScrollElement(element.parentElement);
}

export function getElementAccumulatedScale(element): { scaleX: number, scaleY: number } {
  let scaleX = 1;
  let scaleY = 1;

  while (element && element !== document.documentElement) {
    const style = window.getComputedStyle(element);
    const transform = style.transform;

    if (transform && transform !== 'none') {
      const matrixValues = transform.match(/matrix\(([^)]+)\)/);
      if (matrixValues) {
        const values = matrixValues[1].split(',').map(parseFloat);
        if (values.length === 6) {
          // matrix(a, b, c, d, e, f) where a and d are the scale values
          const [a, , , d] = values;
          scaleX *= a;
          scaleY *= d;
        }
      }
    }

    element = element.parentElement;
  }

  return { scaleX: scaleX, scaleY: scaleY };
}

// This is a waitable wrapper for html element scrollIntoView()
export async function scrollIntoViewAsync(element: HTMLElement, options?: boolean | ScrollIntoViewOptions) {
  return new Promise<void>((resolve) => {
    const handleScroll = () => {
      window.removeEventListener('scroll', handleScroll);
      resolve();
    };

    window.addEventListener('scroll', handleScroll);
    element.scrollIntoView(options);

    // Fallback in case the scroll event doesn't trigger
    setTimeout(() => {
      window.removeEventListener('scroll', handleScroll);
      resolve();
    }, 1000); // Adjust timeout as needed
  });
}

/// Downloads a file with the given filename and data.
export function downloadFile(data: string, filename: string, mimeType: string): void {
  const blob = new Blob([data], { type: mimeType });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/// Clears all cookies.
export function clearAllCookies(): void {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
}

// Encode UTF8 string to Base64 safely (uses browser-specific functions)
export function base64EncodeUtf8String(text: string): string {
  const bytes = new TextEncoder().encode(text);
  const binString = Array.from(bytes, (byte) =>
    String.fromCodePoint(byte)
  ).join("");
  return btoa(binString);
}

export function isElementScrollable(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;

  // Check if overflow-y is scroll or auto, and if the element is scrollable
  const isOverflowScroll = overflowY === 'scroll' || overflowY === 'auto';
  const canScroll = element.scrollHeight > element.clientHeight;

  return isOverflowScroll && canScroll;
}

/** Read a numeric CSS custom property from :root.
 * @param propertyName The CSS custom property name (e.g., '--transition-slide-in-out-ms')
 * @param fallback Fallback value if property is not defined
 * @returns The numeric value of the property */
export function getCssCustomPropertyNumber(propertyName: string, fallback: number): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim();
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/** Get the card transition duration in milliseconds (used for card position and expand/collapse animations).
 * Reads from CSS custom property --card-transition-ms defined in _spacing.scss */
export function getCardTransitionMs(): number {
  return getCssCustomPropertyNumber('--card-transition-ms', 400);
}

/** Get the slide-in-out transition duration in milliseconds.
 * Reads from CSS custom property --transition-slide-in-out-ms defined in _spacing.scss */
export function getTransitionSlideInOutMs(): number {
  return getCssCustomPropertyNumber('--transition-slide-in-out-ms', 350);
}

/** Get the side menu width in pixels.
 * Reads from CSS custom property --side-menu-width defined in _spacing.scss */
export function getSideMenuWidth(): number {
  return getCssCustomPropertyNumber('--side-menu-width', 260);
}

/** Get the collapsed side menu width in pixels.
 * Reads from CSS custom property --side-menu-collapsed-width defined in _spacing.scss */
export function getSideMenuCollapsedWidth(): number {
  return getCssCustomPropertyNumber('--side-menu-collapsed-width', 70);
}

/** Get the card list gap in pixels (spacing between cards in cs-card-list).
 * Reads from CSS custom property --card-list-gap defined in _spacing.scss */
export function getCardListGap(): number {
  return getCssCustomPropertyNumber('--card-list-gap', 12);
}

/** Get the drag wobble animation duration in milliseconds.
 * Reads from CSS custom property --drag-wobble-duration-ms defined in _spacing.scss */
export function getDragWobbleDurationMs(): number {
  return getCssCustomPropertyNumber('--drag-wobble-duration-ms', 300);
}

export function normalizeUrl(browserUrl: string): string {
  browserUrl = browserUrl.trim();
  if (browserUrl.charAt(0) !== '/') {
    // ensure first char is a /
    browserUrl = '/' + browserUrl;
  }
  if (browserUrl.length > 1 && browserUrl.charAt(browserUrl.length - 1) === '/') {
    // ensure last char is not a /
    browserUrl = browserUrl.substring(0, browserUrl.length - 1);
  }

  return browserUrl;
}

/**
 * Helper class for stopping a destroyed component's async callbacks from doing work.
 * NG0911 ("View has already been destroyed") is thrown when a destroy hook is registered on a
 * destroyed view (`afterNextRender`, `DestroyRef.onDestroy`, effects). `markForCheck()` alone
 * cannot throw it in Angular 20 (#5686).
 *
 * For pages extending CsPage, use the built-in `viewDestroyed` flag and `safeTimeout()` instead.
 *
 * @example
 * ```typescript
 * export class MyComponent implements OnDestroy {
 *   private destroyGuard = new DestroyGuard();
 *
 *   ngOnDestroy() {
 *     this.destroyGuard.destroy();
 *   }
 *
 *   async loadDataAsync() {
 *     const data = await this.api.fetchAsync();
 *     if (this.destroyGuard.isDestroyed) return;
 *     this.data = data;
 *     this.cd.markForCheck();
 *   }
 *
 *   startAnimation() {
 *     this.destroyGuard.safeTimeout(() => {
 *       this.animating = false;
 *       this.cd.markForCheck();
 *     }, 300);
 *   }
 * }
 * ```
 */
export class DestroyGuard {
  private _destroyed = false;
  private _pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  /** Returns true if destroy() has been called. */
  get isDestroyed(): boolean {
    return this._destroyed;
  }

  /**
   * Mark as destroyed and cancel all pending timeouts.
   * Call this in ngOnDestroy().
   */
  destroy(): void {
    this._destroyed = true;
    for (const timeoutId of this._pendingTimeouts) {
      clearTimeout(timeoutId);
    }
    this._pendingTimeouts = [];
  }

  /**
   * Safe wrapper for setTimeout that automatically skips callback if destroyed.
   * @returns The timeout ID (can be used with cancelTimeout if needed).
   */
  safeTimeout(callback: () => void, delay: number): ReturnType<typeof setTimeout> {
    const timeoutId = setTimeout(() => {
      const index = this._pendingTimeouts.indexOf(timeoutId);
      if (index > -1) this._pendingTimeouts.splice(index, 1);
      if (!this._destroyed) {
        callback();
      }
    }, delay);
    this._pendingTimeouts.push(timeoutId);
    return timeoutId;
  }

  /** Cancel a specific timeout. */
  cancelTimeout(timeoutId: ReturnType<typeof setTimeout>): void {
    clearTimeout(timeoutId);
    const index = this._pendingTimeouts.indexOf(timeoutId);
    if (index > -1) this._pendingTimeouts.splice(index, 1);
  }

  /**
   * Safe wrapper for markForCheck that skips if destroyed.
   * Use after async operations to prevent NG0911 errors.
   */
  safeMarkForCheck(cd: ChangeDetectorRef): void {
    if (!this._destroyed) {
      cd.markForCheck();
    }
  }

  /**
   * Safe wrapper for waitForAngularUpdate that checks destroyed state after waiting.
   * Returns true if safe to continue, false if view was destroyed during the wait.
   * Caller MUST check the return value and return early when false.
   *
   * The `false` answer only became reachable when `waitForAngularUpdate` was made to
   * return on destroy (df-3f0eaff27ac6). Before that the wait simply never finished, so
   * a component destroyed mid-wait never got an answer at all and every caller behind it
   * hung with it — this method looked like it handled that case and could not.
   */
  async safeWaitForAngularUpdate(injector: Injector): Promise<boolean> {
    if (this._destroyed) return false;
    await waitForAngularUpdate(injector);
    return !this._destroyed;
  }
}
